import { MapsAPILoader } from '@duxor/agm-core';
import { DOCUMENT } from '@angular/common';
import { Component, HostListener, inject, Inject, OnInit, Renderer2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment, metaTags } from '@env/environment';
import { ClipboardService } from '@services/clipboard.service';
import { MobileService } from '@services/mobile.service';
import { first } from 'rxjs/operators';
import { ThemeService } from '@services/theme.service';
import { AuthFacadeService } from '@store/root-store/auth/auth-facade.service';
import { AuthenticatedUser } from '@models/authenticated-user';
import { Themes } from '@staticData/themes';
import { Theme } from '@models/theme';
import { ThemesSuffix } from '@enums/themes';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { arabicViewHandler, getLanguage } from '@services/utils/locale';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ClipboardService],
  standalone: false,
})
export class AppComponent implements OnInit {
  // mobile view
  isMobileView: boolean;
  isMobileDevice: boolean;
  isTabletView: boolean;
  mapLoaded = false;
  sessionExpired = false;
  theme: string;
  deviceHeight: number;
  mobileBreakpoint = 992; // 992
  tabletBreakpoint = 992; //1366;
  logoUrl: string = '';
  allThemes: Theme[] = Themes;
  themeList = Object.values(ThemesSuffix);
  router = inject(Router);

  constructor(
    private translate: TranslateService,
    private mobService: MobileService,
    private mapsAPILoader: MapsAPILoader,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
    private readonly meta: Meta,
    private themeService: ThemeService,
    private metaTagService: Meta,
    private authFacade: AuthFacadeService,
    private _renderer: Renderer2,
  ) {
    this.handleLanguages();
    this.setTitle();
    this.loadMap();
    //this.handleMobileView();
    this.mobService.handleMobileView();
    this.handleThemes();

    this.deviceHeight = window.innerHeight;
    this.document.body.classList.add(environment.bodyClass);
  }

  ngOnInit(): void {
    this.metaTagService.addTags(metaTags);

    this.router.events.subscribe((event: NavigationEnd) => {
      if (event.url) {
        if (event.url.includes('auth/')) {
          // this.handleMobileView(true);
          this.mobService.handleMobileView(true);
          this.meta.updateTag({
            name: 'viewport',
            content: 'width=device-width, initial-scale=1',
          });
        } else if (
          event.url.includes('manager/dashboard') ||
          event.url.includes('live-share/') ||
          event.url.includes('location-history') ||
          event.url.includes('geofence/add-geofence') ||
          event.url.includes('geofence/edit-geofence')
        ) {
          // this.handleMobileView(true);
          this.mobService.handleMobileView(true);
          this.meta.updateTag({
            name: 'viewport',
            content: 'width=device-width, initial-scale=1',
          });
        } else if (event.url.includes('compliance/logs/details') && !event.url.includes('edit-form')) {
          // this.handleMobileView(true);
          this.mobService.handleMobileView(true);
          this.meta.updateTag({
            name: 'viewport',
            content: 'width=device-width, initial-scale=1',
          });
        } else {
          if (!event.url) {
            // this.handleMobileView(false);
            this.mobService.handleMobileView(true);
            this.meta.updateTag({
              name: 'viewport',
              content: 'width=1920, initial-scale=1, minimum-scale=0.1',
            });
          } else {
            // this.handleMobileView();
            this.mobService.handleMobileView();
            this.meta.updateTag({
              name: 'viewport',
              content: 'width=1920, initial-scale=1, minimum-scale=0.1',
            });
          }
        }
      }
    });
  }

  setTitle() {
    this.titleService.setTitle(environment.appName);
  }

  setLogo() {
    this.logoUrl = `${environment.assetsBaseUrl}${environment.assetsVersion}/images/${this.theme}/${environment.images.logoImg}`;
  }

  loadMap() {
    this.mapsAPILoader.load().then(() => {
      this.mapLoaded = true;
    });
  }

  handleLanguages(): void {
    arabicViewHandler();
    this.translate.setDefaultLang(getLanguage());
  }

  @HostListener('window:focus', ['$event'])
  onFocus(): void {
    this.checkTokens();
  }

  handleMobileView(showMobileView: boolean = false) {
    if (showMobileView) {
      if (window.screen.width < this.mobileBreakpoint) {
        this.mobService.setIsMobileDevice(true);
        this.mobService.setMobileViewState(true, true);
        this.document.body.classList.add('eld-is--mobile');
      } else {
        this.mobService.setIsMobileDevice(false);
        this.mobService.setMobileViewState(false, true);
        this.document.body.classList.remove('eld-is--mobile');
        if (window.screen.width >= this.mobileBreakpoint && window.screen.width < this.tabletBreakpoint) {
          this.mobService.setTabletViewState(true);
        } else {
          this.mobService.setTabletViewState(false);
        }
      }
    } else {
      this.mobService.setIsMobileDevice(false);
      this.mobService.setMobileViewState(false);
      this.document.body.classList.remove('eld-is--mobile');
    }

    this.isMobileView = this.mobService.fetchMobileViewState();
    this.isMobileDevice = this.mobService.fetchIsMobileDeviceState();
    this.isTabletView = this.mobService.fetchTabletViewState();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    const height = event.target.innerHeight;
    const width = event.target.innerWidth;

    const urlEvent = event.url;
    const urlCurrentLocation = event.currentTarget.location.hash;
    this.mobService.metaTagUpdater(height, width, urlCurrentLocation, urlEvent);

    // if (this.isMobileDevice && this.deviceHeight > height && width > height) {
    //   this.meta.updateTag({
    //     name: 'viewport',
    //     content: `height=${this.deviceHeight}px, width=device-width, initial-scale=1`,
    //   });
    // } else {
    //   const url = event.currentTarget.location.hash;
    //   if (url.includes('auth/')) {
    //     this.meta.updateTag({
    //       name: 'viewport',
    //       content: 'width=device-width, initial-scale=1',
    //     });
    //   } else if (
    //     url.includes('manager/dashboard') ||
    //     url.includes('live-share/') ||
    //     url.includes('location-history') ||
    //     url.includes('geofence/add-geofence') ||
    //     url.includes('geofence/edit-geofence')
    //   ) {
    //     this.meta.updateTag({
    //       name: 'viewport',
    //       content: 'width=device-width, initial-scale=1',
    //     });
    //   } else if (
    //     url.includes('compliance/logs/details') &&
    //     !url.includes('edit-form')
    //   ) {
    //     this.meta.updateTag({
    //       name: 'viewport',
    //       content: 'width=device-width, initial-scale=1',
    //     });
    //   } else {
    //     if (event.url && !event.url.includes('#')) {
    //       // this.handleMobileView(true);
    //       this.mobService.handleMobileView(true);
    //       this.meta.updateTag({
    //         name: 'viewport',
    //         content: 'width=device-width, initial-scale=1',
    //       });
    //     } else {
    //       if (!event.url) {
    //         // this.handleMobileView(false);
    //         this.mobService.handleMobileView(false);
    //         setTimeout(() =>{
    //           const hash = window.location.hash;
    //           if (hash == '#/auth/login') {
    //             this.meta.updateTag({
    //               name: 'viewport',
    //               content: 'width=device-width, initial-scale=1',
    //             });
    //           } else {
    //             this.meta.updateTag({
    //               name: 'viewport',
    //               content: 'width=1920, initial-scale=1, minimum-scale=0.1',
    //             });
    //           }
    //
    //         }, 300)
    //
    //         // this.handleMobileView(false);
    //         this.mobService.handleMobileView(false);
    //         this.meta.updateTag({
    //           name: 'viewport',
    //           content: 'width=1920, initial-scale=1, minimum-scale=0.1',
    //         });
    //       } else {
    //         // this.handleMobileView();
    //         this.mobService.handleMobileView();
    //         this.meta.updateTag({
    //           name: 'viewport',
    //           content: 'width=1920, initial-scale=1, minimum-scale=0.1',
    //         });
    //       }
    //     }
    //   }
    // }
  }

  handleThemes() {
    const selectedTheme = this.themeService.theme$$.value;
    const selectedThemeFromStorage: Theme = this.allThemes.find((theme: Theme) => theme.id === selectedTheme);

    this.document.body.classList.add(selectedThemeFromStorage.cssClass);
    this.theme = selectedThemeFromStorage.id;

    // Theme from storage
    this.allThemes.forEach((theme: Theme) => {
      if (theme.id !== selectedThemeFromStorage.id) {
        this.document.body.classList.remove(theme.cssClass);
      }
    });

    this.themeService.getTheme().subscribe((state: string) => {
      const selectedTheme: Theme = this.allThemes.find((theme: Theme) => theme.id === state);
      this.theme = selectedTheme.id;
      this.document.body.classList.add(selectedTheme.cssClass);

      this.allThemes.forEach((theme: Theme) => {
        if (theme.id !== selectedTheme.id) {
          this.document.body.classList.remove(theme.cssClass);
        }
      });

      this.setLogo();
    });
  }

  checkTokens() {
    if (!document.hidden) {
      const userStorage = localStorage.getItem('user');
      const user = JSON.parse(userStorage);
      if (user) {
        const storageToken = user.token;
        if (!storageToken) {
          return (this.sessionExpired = false);
        }

        this.authFacade.user$.pipe(first()).subscribe((user: AuthenticatedUser | null) => {
          const memoryToken = user?.token;
          if (memoryToken) {
            this.sessionExpired = memoryToken !== storageToken;
          }
        });
      }
    }
  }

  onReload() {
    location.reload();
  }

  // Theme Switch with ALT + T
  @HostListener('window:keydown', ['$event'])
  keyEvent(event) {
    if (event.code === 'KeyT' && event.altKey) {
      event.stopPropagation();
      event.preventDefault();
      this.switchTheme();
    }
  }

  switchTheme() {
    const themeSum: number = this.themeList.length;
    let newThemeIndex: number = this.themeList.findIndex((x: string) => x === this.theme) + 1;
    if (newThemeIndex >= themeSum) {
      newThemeIndex = 0;
    }

    const nextTheme: string = this.themeList[newThemeIndex];
    this.theme = nextTheme;
    this.themeService.setTheme(this.theme);
    localStorage.setItem('theme', nextTheme);
  }
}
